import { type QueryKey, useQueryClient } from "@tanstack/react-query";

// A read that began before a write cannot describe what the record holds after it.
//
// TanStack answers a refetch with the request already in flight whenever the query has no data
// yet (query-core query.js: the in-flight promise is returned unless `state.data !== undefined`),
// so a screen whose first load is still running refreshes into the list as it was before the save.
// What was just written is then missing from the only place anybody can check it, and the person
// who saved it is told nothing at all. Cancelling the older read first means the refresh issues
// its own request, so what the screen settles on is what the server holds after the write.
//
// This is deliberately not a refetch on the query object: the call site holds the observer, not
// the cache, and only the cache can retire a request that is already in the air.
export function useRefreshAfterWrite(): (queryKey: QueryKey) => Promise<void> {
  const client = useQueryClient();
  return async (queryKey) => {
    await client.cancelQueries({ queryKey });
    await client.refetchQueries({ queryKey });
  };
}
